// アカウント管理 — このアプリにログインできる人（メンバー）の一覧・追加・権限変更・削除。
//
// 権限は2段階:
//   管理者(admin) … メンバーの追加/権限変更/削除ができる
//   一般(member)  … 月次処理の操作はすべて可能。メンバーの管理だけができない
// ドライバー（承認する人）とは別concept。ドライバーは「マスター編集」で管理する。

import React, { useEffect, useState } from "react";
import { Ic } from "../components/Icons";
import { useAuth } from "../auth";
import {
  addMember,
  fetchMembers,
  removeMember,
  updateMember,
  type Member,
  type MemberRole,
} from "../lib/authApi";

export function AccountAdminScreen({ onNav }: { onNav: (s: string) => void }) {
  const { member: me, authEnabled, isAdmin } = useAuth();
  const [members, setMembers] = useState<Member[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null); // 操作中のメアド
  const [toast, setToast] = useState<{ tone: "ok" | "warn"; text: string } | null>(null);

  // 追加フォーム
  const [showAdd, setShowAdd] = useState(false);
  const [fEmail, setFEmail] = useState("");
  const [fName, setFName] = useState("");
  const [fOrg, setFOrg] = useState("");
  const [fRole, setFRole] = useState<MemberRole>("member");
  const [fInvite, setFInvite] = useState(true);
  const [adding, setAdding] = useState(false);

  const load = async () => {
    const r = await fetchMembers();
    setLoading(false);
    if (!r.ok) {
      setError(r.error || "メンバー一覧を取得できませんでした");
      return;
    }
    setError(null);
    setMembers(r.members);
  };

  useEffect(() => {
    void load();
  }, []);

  const notify = (tone: "ok" | "warn", text: string) => {
    setToast({ tone, text });
    setTimeout(() => setToast(null), 4000);
  };

  const submitAdd = async (e: React.FormEvent) => {
    e.preventDefault();
    setAdding(true);
    const r = await addMember({ email: fEmail, name: fName, org: fOrg, role: fRole, invite: fInvite });
    setAdding(false);
    if (!r.ok) {
      notify("warn", r.error || "追加に失敗しました");
      return;
    }
    notify(
      "ok",
      fInvite
        ? r.invited
          ? `${fName} さんを追加し、ログイン用リンクをメールで送りました`
          : `${fName} さんを追加しました（招待メールは送れませんでした：${r.inviteError || "設定を確認してください"}）`
        : `${fName} さんを追加しました`
    );
    setShowAdd(false);
    setFEmail("");
    setFName("");
    setFOrg("");
    setFRole("member");
    setFInvite(true);
    await load();
  };

  const changeRole = async (m: Member, role: MemberRole) => {
    setBusy(m.email);
    const r = await updateMember({ email: m.email, role });
    setBusy(null);
    if (!r.ok) return notify("warn", r.error || "変更に失敗しました");
    notify("ok", `${m.name} さんを${role === "admin" ? "管理者" : "一般"}に変更しました`);
    await load();
  };

  const toggleDisabled = async (m: Member) => {
    const next = !m.disabled;
    if (next && !window.confirm(`${m.name} さんの利用を停止しますか？（ログインできなくなります）`)) return;
    setBusy(m.email);
    const r = await updateMember({ email: m.email, disabled: next });
    setBusy(null);
    if (!r.ok) return notify("warn", r.error || "変更に失敗しました");
    notify("ok", `${m.name} さんを${next ? "利用停止" : "再開"}しました`);
    await load();
  };

  const doRemove = async (m: Member) => {
    if (!window.confirm(`${m.name} さん（${m.email}）を削除しますか？\nこのアプリにログインできなくなります（業務データは消えません）。`))
      return;
    setBusy(m.email);
    const r = await removeMember(m.email);
    setBusy(null);
    if (!r.ok) return notify("warn", r.error || "削除に失敗しました");
    notify("ok", `${m.name} さんを削除しました`);
    await load();
  };

  return (
    <div className="content" style={{ maxWidth: 1000 }}>
      <div className="page-title-row">
        <div className="col gap-4">
          <div className="page-title">アカウント管理</div>
          <div className="page-sub muted">
            このアプリ（事務所側の画面）にログインできる人の一覧です。ドライバーの登録は「マスター編集」で行います。
          </div>
        </div>
        <div style={{ flex: 1 }} />
        <button className="btn" onClick={() => onNav("home")}>
          <Ic.chevronL size={13} /> ホーム
        </button>
        {isAdmin && (
          <button className="btn primary" onClick={() => setShowAdd((v) => !v)}>
            <Ic.plus size={14} /> メンバーを追加
          </button>
        )}
      </div>

      {toast && (
        <div
          className={toast.tone === "ok" ? "toast" : "toast warn"}
          style={toast.tone === "ok" ? { background: "#e8f7ec", borderColor: "#a8dbb1", color: "#1d6b34", marginBottom: 14 } : { marginBottom: 14 }}
        >
          {toast.tone === "ok" ? <Ic.checkCircle size={14} /> : <Ic.alert size={14} />}
          <span className="tiny">{toast.text}</span>
        </div>
      )}

      {!authEnabled && (
        <div className="toast warn" style={{ marginBottom: 14 }}>
          <Ic.alert size={14} />
          <div className="col gap-4">
            <b>この環境ではログイン機能が動作していません</b>
            <div className="tiny">
              ローカル開発環境、または Cloudflare の KV が未設定の可能性があります。本番URLでご確認ください。
            </div>
          </div>
        </div>
      )}

      {/* 追加フォーム */}
      {showAdd && isAdmin && (
        <form className="card" style={{ padding: "18px 22px", marginBottom: 16 }} onSubmit={submitAdd}>
          <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 12 }}>メンバーを追加</div>
          <div className="row gap-8" style={{ flexWrap: "wrap", alignItems: "flex-end" }}>
            <Field label="氏名" required>
              <input className="input" required value={fName} onChange={(e) => setFName(e.target.value)} placeholder="山田 太郎" style={{ width: 160 }} />
            </Field>
            <Field label="メールアドレス" required>
              <input className="input" type="email" required value={fEmail} onChange={(e) => setFEmail(e.target.value)} placeholder="yamada@example.co.jp" style={{ width: 240 }} />
            </Field>
            <Field label="所属">
              <input className="input" value={fOrg} onChange={(e) => setFOrg(e.target.value)} placeholder="株式会社ICB" style={{ width: 180 }} />
            </Field>
            <Field label="権限">
              <select className="input" value={fRole} onChange={(e) => setFRole(e.target.value as MemberRole)} style={{ width: 120 }}>
                <option value="member">一般</option>
                <option value="admin">管理者</option>
              </select>
            </Field>
            <label className="row gap-4 tiny" style={{ alignItems: "center", paddingBottom: 8, cursor: "pointer" }}>
              <input type="checkbox" checked={fInvite} onChange={(e) => setFInvite(e.target.checked)} />
              ログイン用リンクをすぐメールで送る
            </label>
            <button className="btn primary" type="submit" disabled={adding} style={{ marginBottom: 2 }}>
              <Ic.check size={14} /> {adding ? "追加中..." : "追加する"}
            </button>
            <button className="btn" type="button" onClick={() => setShowAdd(false)} style={{ marginBottom: 2 }}>
              キャンセル
            </button>
          </div>
          <div className="tiny muted" style={{ marginTop: 10 }}>
            <b>管理者</b>＝メンバーの追加・削除ができます／<b>一般</b>＝月次処理の操作はすべて可能ですが、メンバーの管理はできません。
          </div>
        </form>
      )}

      {loading ? (
        <div className="toast info">
          <Ic.info size={14} /> 読み込み中...
        </div>
      ) : error ? (
        <div className="toast warn">
          <Ic.alert size={14} />
          <span className="tiny">{error}</span>
        </div>
      ) : (
        <div className="card" style={{ padding: "6px 0 0" }}>
          <div className="tbl-scroll">
            <table className="tbl">
              <thead>
                <tr>
                  <th>氏名</th>
                  <th>メールアドレス</th>
                  <th>所属</th>
                  <th>権限</th>
                  <th>最終ログイン</th>
                  <th style={{ textAlign: "right" }}>操作</th>
                </tr>
              </thead>
              <tbody>
                {members.map((m) => {
                  const isSelf = me?.email === m.email;
                  return (
                    <tr key={m.email} style={m.disabled ? { opacity: 0.55 } : undefined}>
                      <td style={{ fontWeight: 700 }}>
                        {m.name}
                        {isSelf && <span className="chip" style={{ marginLeft: 6 }}>あなた</span>}
                        {m.disabled && <span className="chip amber" style={{ marginLeft: 6 }}>利用停止</span>}
                      </td>
                      <td style={{ fontFamily: "monospace", fontSize: 12 }}>{m.email}</td>
                      <td className="tiny">{m.org || "—"}</td>
                      <td>
                        {isAdmin && !isSelf ? (
                          <select
                            className="input"
                            style={{ padding: "3px 6px", fontSize: 12, width: 96 }}
                            value={m.role}
                            disabled={busy === m.email}
                            onChange={(e) => changeRole(m, e.target.value as MemberRole)}
                          >
                            <option value="member">一般</option>
                            <option value="admin">管理者</option>
                          </select>
                        ) : m.role === "admin" ? (
                          <span className="chip">
                            <Ic.shield size={11} /> 管理者
                          </span>
                        ) : (
                          <span className="tiny muted">一般</span>
                        )}
                      </td>
                      <td className="tiny muted">{m.lastLoginAt ? fmtDate(m.lastLoginAt) : "未ログイン"}</td>
                      <td style={{ textAlign: "right", whiteSpace: "nowrap" }}>
                        {isAdmin && !isSelf && (
                          <>
                            <button
                              className="btn sm ghost"
                              disabled={busy === m.email}
                              onClick={() => toggleDisabled(m)}
                              title={m.disabled ? "ログインを再開します" : "ログインを停止します（履歴は残ります）"}
                            >
                              {m.disabled ? "再開" : "利用停止"}
                            </button>
                            <button
                              className="btn sm ghost"
                              disabled={busy === m.email}
                              onClick={() => doRemove(m)}
                              style={{ marginLeft: 6, color: "#cc2f2a" }}
                            >
                              <Ic.trash size={12} /> 削除
                            </button>
                          </>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <div className="card" style={{ padding: "16px 20px", marginTop: 16 }}>
        <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 8 }}>ログインの仕組み</div>
        <ul className="tiny muted" style={{ margin: 0, paddingLeft: 18, lineHeight: 1.9 }}>
          <li>パスワードはありません。ログイン画面でメールアドレスを入力すると、ワンタイムURL（15分有効）が届きます</li>
          <li>一度ログインすると30日間はログインしたままです（ブラウザごと）</li>
          <li>ここに登録されていないアドレスではログインできません。退任時は「削除」または「利用停止」してください</li>
          <li>ドライバーの承認画面（本人専用URL）とメアド登録フォームは、これまでどおりログイン不要で開けます</li>
        </ul>
      </div>
    </div>
  );
}

function Field({ label, required, children }: { label: string; required?: boolean; children: React.ReactNode }) {
  return (
    <div className="col gap-4" style={{ marginBottom: 6 }}>
      <div className="tiny" style={{ fontWeight: 700 }}>
        {label}
        {required && <span style={{ color: "#cc2f2a" }}> *</span>}
      </div>
      {children}
    </div>
  );
}

function fmtDate(iso: string): string {
  const d = new Date(iso);
  if (isNaN(d.getTime())) return "—";
  const p = (n: number) => String(n).padStart(2, "0");
  return `${p(d.getMonth() + 1)}/${p(d.getDate())} ${p(d.getHours())}:${p(d.getMinutes())}`;
}
